import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../configuration';
import { getCabPlusStatus } from '../../api/cab-plus/cab-plus.api';

// Kết quả Cab+ Pro theo từng tài khoản, dùng chung cho mọi chỗ cần (gọi máy chủ một lần mỗi tài khoản mỗi phiên).
const pending = new Map<string, Promise<boolean>>();
// Giá trị đã biết, đọc đồng bộ được để lần vẽ đầu đúng luôn (banner không chớp lên với người có Pro khi chuyển trang).
const resolved = new Map<string, boolean>();

const fetchIsPro = (userId: string) => {
  let p = pending.get(userId);
  if (!p) {
    p = getCabPlusStatus()
      .then((s) => {
        const v = !!s?.isPremium;
        if (pending.get(userId) === p) resolved.set(userId, v);
        return v;
      })
      .catch(() => {
        if (pending.get(userId) === p) pending.delete(userId); // lỗi thì lần sau hỏi lại
        return false;
      });
    pending.set(userId, p);
  }
  return p;
};

/** Xoá kết quả đã nhớ, ví dụ ngay sau khi mua hoặc nâng gói Cab+, để lần sau hỏi lại máy chủ. */
export const clearViewerProCache = () => {
  pending.clear();
  resolved.clear();
};

/** Giữ tên cũ cho test. */
export const resetViewerProCache = clearViewerProCache;

export interface ViewerProStatus {
  /** Người xem đang có Cab+ Pro. */
  isPro: boolean;
  /**
   * Đã biết chắc kết quả chưa: khách (chưa có hồ sơ) luôn là đã biết; tài khoản đã đăng nhập thì chỉ đã biết khi máy chủ
   * trả lời (lỗi cũng tính là đã biết, coi như không có Pro, để chỗ đang chờ không chờ mãi).
   */
  known: boolean;
}

const statusOf = (userId?: string): ViewerProStatus => {
  if (!userId) return { isPro: false, known: true };
  const v = resolved.get(userId);
  return v === undefined ? { isPro: false, known: false } : { isPro: v, known: true };
};

/**
 * Trạng thái Cab+ Pro của người xem kèm cờ đã biết hay chưa. Chỗ nào cần chờ biết chắc rồi mới làm (ví dụ banner trang chủ
 * chờ rồi mới hỏi quảng cáo) dùng hook này; chỗ chỉ cần ẩn đi dùng useViewerIsPro.
 */
export const useViewerProStatus = (): ViewerProStatus => {
  const userId = useSelector((s: RootState) => s.features.auth.profile?.userId);
  const [status, setStatus] = useState<ViewerProStatus>(() => statusOf(userId));
  useEffect(() => {
    let alive = true;
    setStatus(statusOf(userId));
    if (!userId) return undefined;
    fetchIsPro(userId).then((v) => alive && setStatus({ isPro: v, known: true }));
    return () => {
      alive = false;
    };
  }, [userId]);
  return status;
};

/**
 * Người xem đang có Cab+ Pro hay không. Đã biết từ trước thì trả ngay, chưa biết thì trả false cho tới khi máy chủ trả lời,
 * để chỗ gọi không phải chờ mới vẽ. Khách chưa đăng nhập luôn là false.
 */
export const useViewerIsPro = (): boolean => useViewerProStatus().isPro;
